import { useCallback, useState, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Send,
  MousePointerClick,
  Percent,
  ShieldAlert,
  Radio,
  Inbox,
  X,
  ChevronRight,
} from 'lucide-react';
import { api } from '../api';
import { usePolling } from '../hooks/usePolling';
import { useOrg } from '../auth/OrgContext';
import {
  Card,
  PageHeader,
  StatCard,
  Badge,
  statusTone,
  EmptyState,
  CHART_PALETTE,
} from '../components/ui';
import {
  FUNNEL_STAGES,
  countersFromFunnel,
  funnelFromEvents,
  timeSeries,
  type EventLite,
} from '../lib/aggregate';

// The events API returns more than the funnel needs — keep the extra fields for the drill-down.
interface FeedEvent extends EventLite {
  campaign_id?: string;
  org_id?: string;
  received_at?: string;
  seq?: number;
  metadata?: Record<string, unknown>;
}

const EVENT_TYPES = [
  'sent',
  'opened',
  'clicked',
  'submitted_data',
  'reported',
  'bounced',
  'replied',
];
const WINDOW_MS: Record<string, number> = { '15m': 900_000, '1h': 3_600_000, '24h': 86_400_000 };

function EventDrawer({ e, onClose }: { e: FeedEvent; onClose: () => void }) {
  const rows: [string, string][] = [
    ['Event ID', e.id],
    ['Type', e.type],
    ['Simulation', e.simulation_id],
    ['Campaign', e.campaign_id ?? '—'],
    ['Org', e.org_id ?? '—'],
    ['Occurred at', new Date(e.occurred_at).toLocaleString()],
    ['Received at', e.received_at ? new Date(e.received_at).toLocaleString() : '—'],
    ['Sequence', e.seq != null ? String(e.seq) : '—'],
  ];
  const meta = e.metadata && Object.keys(e.metadata).length > 0 ? e.metadata : null;
  return (
    <div className="fixed inset-0 z-30 flex justify-end">
      <div className="absolute inset-0 bg-ink/20" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-auto border-l border-line bg-paper">
        <div className="flex items-center justify-between border-b border-line px-6 py-5">
          <div className="flex items-center gap-2">
            <Badge tone={statusTone(e.type)}>{e.type}</Badge>
            <span className="font-display text-lg text-ink">Event detail</span>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink" title="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5">
          <Card className="divide-y divide-line">
            {rows.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
                <span className="shrink-0 text-muted">{k}</span>
                <span className="truncate text-right font-mono text-xs text-ink">{v}</span>
              </div>
            ))}
          </Card>
          <div className="eyebrow mb-2 mt-6">Metadata</div>
          <pre className="overflow-x-auto rounded-input border border-line bg-ink/[0.02] p-4 font-mono text-xs text-ink">
            {meta ? JSON.stringify(meta, null, 2) : 'No metadata. Credentials are never stored.'}
          </pre>
        </div>
      </div>
    </div>
  );
}

const fmtTime = (t: number) =>
  new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Monochrome ramp (light → ink) — the funnel narrows visually darker; no colour (theme brief).
// FR-8: chart SERIES use the shared rainbow palette; the rest of the dashboard stays monochrome.
const AREA = {
  sent: CHART_PALETTE[0],
  opened: CHART_PALETTE[1],
  clicked: CHART_PALETTE[3],
} as const;

const tooltipStyle = {
  borderRadius: 10,
  border: '1px solid #e6e6e6',
  boxShadow: '0 1px 2px rgb(10 10 10 / 0.06)',
  fontSize: 12,
  fontFamily: 'Inter Variable, Inter, sans-serif',
};

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-5">
      <div className="eyebrow mb-4">{title}</div>
      {children}
    </Card>
  );
}

// Live operator dashboard (PRD §9.3): counters + time-series + funnel + live Event feed, polling
// the Events API every 5s. The data layer is pure (lib/aggregate, unit-tested); this only renders.
export function DashboardPage() {
  const { withOrg, selectedOrgId, selectedOrg } = useOrg();
  const fetchEvents = useCallback(
    () => api.get<{ data: FeedEvent[] }>(withOrg('/v1/events?limit=100')),
    [withOrg],
  );
  const { data, error } = usePolling(fetchEvents, 5000, [selectedOrgId]);
  const events = data?.data ?? [];

  const [feedType, setFeedType] = useState('');
  const [feedWindow, setFeedWindow] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState<FeedEvent | null>(null);

  const funnel = funnelFromEvents(events);
  const counters = countersFromFunnel(funnel);
  const series = timeSeries(events).map((b) => ({ ...b, label: fmtTime(b.t) }));
  const funnelData = FUNNEL_STAGES.map((stage) => ({ stage, count: funnel[stage] }));

  // Live feed: newest first, filtered by type + time window, capped at 25.
  const cutoff = WINDOW_MS[feedWindow] ? Date.now() - WINDOW_MS[feedWindow]! : 0;
  const feed = events
    .filter(
      (e) => (!feedType || e.type === feedType) && (!cutoff || Date.parse(e.occurred_at) >= cutoff),
    )
    .slice()
    .sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))
    .slice(0, 25);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={
          selectedOrg
            ? `Live engagement for ${selectedOrg.name}.`
            : 'Live engagement across all your Orgs.'
        }
        actions={
          <span className="inline-flex items-center gap-2 rounded-pill border border-line px-3 py-1.5 text-xs font-medium text-muted">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink opacity-40" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-ink" />
            </span>
            Live · refreshes every 5s
          </span>
        }
      />

      {error && (
        <div className="mb-4 rounded-input border border-line bg-ink/[0.03] px-3 py-2 text-sm text-muted">
          Couldn’t refresh: {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Sent" value={String(counters.sent)} icon={Send} />
        <StatCard label="Clicked" value={String(counters.clicked)} icon={MousePointerClick} />
        <StatCard
          label="Click rate"
          value={`${Math.round(counters.clickRate * 100)}%`}
          icon={Percent}
          hint="of delivered simulations"
        />
        <StatCard label="Reported" value={String(counters.reported)} icon={ShieldAlert} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <ChartCard title="Activity over time">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={series} margin={{ left: -20, right: 8, top: 4 }}>
                <defs>
                  {(['sent', 'opened', 'clicked'] as const).map((k) => (
                    <linearGradient key={k} id={`g-${k}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={AREA[k]} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={AREA[k]} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  fontSize={11}
                  stroke="#6b6b6b"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  fontSize={11}
                  stroke="#6b6b6b"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Area
                  type="monotone"
                  dataKey="sent"
                  stroke={AREA.sent}
                  strokeWidth={2}
                  fill="url(#g-sent)"
                />
                <Area
                  type="monotone"
                  dataKey="opened"
                  stroke={AREA.opened}
                  strokeWidth={2}
                  fill="url(#g-opened)"
                />
                <Area
                  type="monotone"
                  dataKey="clicked"
                  stroke={AREA.clicked}
                  strokeWidth={2}
                  fill="url(#g-clicked)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <div className="lg:col-span-2">
          <ChartCard title="Conversion funnel">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={funnelData} margin={{ left: -20, right: 8, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="stage"
                  fontSize={10}
                  stroke="#6b6b6b"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  fontSize={11}
                  stroke="#6b6b6b"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(10,10,10,0.03)' }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {funnelData.map((_, i) => (
                    <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      <Card className="mt-4">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
          <div className="eyebrow flex items-center gap-2">
            <Radio className="h-4 w-4 text-ink" /> Live event feed
          </div>
          <div className="flex items-center gap-2">
            <select
              value={feedType}
              onChange={(e) => setFeedType(e.target.value)}
              className="input h-8 w-36 cursor-pointer py-1 text-xs"
            >
              <option value="">All types</option>
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              value={feedWindow}
              onChange={(e) => setFeedWindow(e.target.value)}
              className="input h-8 w-28 cursor-pointer py-1 text-xs"
            >
              <option value="all">All time</option>
              <option value="15m">Last 15m</option>
              <option value="1h">Last 1h</option>
              <option value="24h">Last 24h</option>
            </select>
          </div>
        </div>
        {feed.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No matching events"
            hint="Adjust the filters, or activity will stream in as recipients engage."
          />
        ) : (
          <ul className="divide-y divide-line">
            {feed.map((e) => (
              <li key={e.id}>
                <button
                  onClick={() => setSelectedEvent(e)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-3 text-left text-sm transition-colors hover:bg-ink/[0.02]"
                >
                  <span className="truncate font-mono text-xs text-muted">{e.simulation_id}</span>
                  <Badge tone={statusTone(e.type)}>{e.type}</Badge>
                  <span className="flex shrink-0 items-center gap-1 text-xs tabular-nums text-muted">
                    {fmtTime(Date.parse(e.occurred_at))}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {selectedEvent && <EventDrawer e={selectedEvent} onClose={() => setSelectedEvent(null)} />}
    </div>
  );
}

export function PlaceholderPage({ title }: { title: string }) {
  return <PageHeader title={title} subtitle="Coming in a later portal sub-unit." />;
}
